import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Tag, Library, List } from 'lucide-react';
import { api, unwrapApiResponse } from '@/utils/api';
import Navbar from '@/components/layout/Navbar';
import { useAuth } from '@/context/AuthContext';

import { PerfilHeader, type PerfilRankingPosicao } from '@/components/perfil/PerfilHeader';
import { PerfilTabs } from '@/components/perfil/PerfilTabs';
import { PerfilColecaoTab } from '@/components/perfil/PerfilColecaoTab';
import { PerfilListasPublicasTab } from '@/components/perfil/PerfilListasPublicasTab';
import ChapterComments from '@/components/capitulo/ChapterComments';
import { PerfilTagsTab } from '@/components/perfil/PerfilTagsTab';

/** Corpo de `GET usuarios/perfil?nick=` (manhwa-api). */
type PerfilPublicUser = {
    usr_id: number;
    usr_nome: string;
    usr_nick: string;
    usr_imagem?: string | null;
    usr_banner?: string | null;
    usr_criado_em: string;
    usr_nivel?: number;
    usr_titulo?: string | null;
    usr_xp?: number;
    usr_descricao?: string | null;
    tags?: any[];
    moldura?: { mol_id: number; mol_nome: string; mol_imagem: string; mol_tipo: number } | null;
    is_vip?: boolean;
    seguidores?: number;
    seguindo?: number;
    isFollowing?: boolean;
    favorites?: Array<{
        obr_id: number;
        obr_nome: string;
        obr_imagem?: string | null;
        formato?: { formt_nome: string };
    }>;
};

type PerfilData = {
    user: PerfilPublicUser;
};

interface PerfilPageProps {
    data?: PerfilData;
    nick?: string;
}

const TABS = [
    { id: 'colecao', label: 'Coleção', icon: Library },
    { id: 'listas', label: 'Listas', icon: List },
    { id: 'comentarios', label: 'Comentários', icon: MessageSquare },
    { id: 'tags', label: 'Tags', icon: Tag },
];

export default function PerfilPage({ data: initialData }: PerfilPageProps) {
    const { nick } = useParams<{ nick: string }>();
    const navigate = useNavigate();
    const { user: authUser } = useAuth();
    const [data, setData] = useState<PerfilData | null>(initialData ?? null);
    const [loading, setLoading] = useState(!initialData && !!nick);
    const [notFoundState, setNotFoundState] = useState(false);

    const [activeTab, setActiveTab] = useState<string>('colecao');
    const [rankingPosicao, setRankingPosicao] = useState<PerfilRankingPosicao | null>(null);
    const [commentsPage, setCommentsPage] = useState(1);
    const [commentsFeedMeta, setCommentsFeedMeta] = useState({ totalItems: 0, totalPages: 1 });
    const [followLoading, setFollowLoading] = useState(false);

    const fetchGenerationRef = useRef(0);

    /** Sem isto, ao mudar só o `:nick` na URL o mesmo componente mantém o estado do perfil anterior até o fetch terminar (flash/errado). */
    useLayoutEffect(() => {
        if (!nick || initialData) return;
        fetchGenerationRef.current += 1;
        setData(null);
        setLoading(true);
        setNotFoundState(false);
        setRankingPosicao(null);
        setCommentsPage(1);
        setCommentsFeedMeta({ totalItems: 0, totalPages: 1 });
    }, [nick, initialData]);

    useEffect(() => {
        if (!nick || initialData) return;
        const gen = fetchGenerationRef.current;
        let cancelled = false;
        const requestedNick = nick.trim();

        (async () => {
            try {
                const res = await api.get(`usuarios/perfil?nick=${encodeURIComponent(requestedNick)}`, {
                    requiresAuth: true,
                });
                const body = unwrapApiResponse<PerfilPublicUser | null>(res);
                if (cancelled || gen !== fetchGenerationRef.current) return;
                if (body && typeof body.usr_id === 'number') {
                    if (body.usr_nick?.trim().toLowerCase() !== requestedNick.toLowerCase()) {
                        setNotFoundState(true);
                        return;
                    }
                    setData({ user: body });
                } else {
                    setNotFoundState(true);
                }
            } catch (e: unknown) {
                if (cancelled || gen !== fetchGenerationRef.current) return;
                const status = (e as { response?: { status?: number } })?.response?.status;
                if (status === 401) {
                    navigate(`/entrar?next=${encodeURIComponent(`/perfil/${nick}`)}`);
                    return;
                }
                setNotFoundState(true);
            } finally {
                if (!cancelled && gen === fetchGenerationRef.current) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [nick, initialData, navigate]);

    useEffect(() => {
        const n = data?.user?.usr_nick;
        if (!n) return;
        let cancelled = false;
        (async () => {
            try {
                const d: any = unwrapApiResponse(
                    await api.get(`xp/ranking/posicao?nick=${encodeURIComponent(n)}`, { requiresAuth: false }),
                );
                if (cancelled || typeof d?.posicao_global !== 'number') return;
                setRankingPosicao({
                    posicao_global: d.posicao_global,
                    posicao_semanal: typeof d.posicao_semanal === 'number' ? d.posicao_semanal : null,
                });
            } catch {
                if (!cancelled) setRankingPosicao(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [data?.user?.usr_nick]);

    /** Total de comentários no site para as estatísticas do cabeçalho (a aba só monta quando ativa). */
    useEffect(() => {
        const id = data?.user?.usr_id;
        if (!id) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(`comentarios/perfil/${id}?page=1&limit=1`, {
                    requiresAuth: true,
                });
                const body = unwrapApiResponse<{ pagination?: { totalItems?: number } }>(res);
                if (cancelled) return;
                const t = Number(body?.pagination?.totalItems ?? 0);
                setCommentsFeedMeta(prev => ({ ...prev, totalItems: t }));
            } catch {
                if (!cancelled) setCommentsFeedMeta(prev => ({ ...prev, totalItems: 0 }));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [data?.user?.usr_id]);

    const groupedTags = useMemo(() => {
        const userTags = data?.user?.tags;
        if (!userTags) return {};
        const groups: Record<string, any[]> = {};

        userTags.forEach((tag: any) => {
            const pacote = tag.tag_pacote || 'Geral';
            if (!groups[pacote]) groups[pacote] = [];
            groups[pacote].push(tag);
        });

        return groups;
    }, [data?.user?.tags]);

    const isOwnProfile = Boolean(
        authUser?.usr_id && data?.user?.usr_id && authUser.usr_id === data.user.usr_id,
    );

    const handleFollowToggle = useCallback(async () => {
        const u = data?.user;
        if (!u?.usr_id || isOwnProfile) return;
        setFollowLoading(true);
        try {
            const res = await api.post(`usuarios/${u.usr_id}/seguir`, {}, { requiresAuth: true });
            const body = unwrapApiResponse<{ following?: boolean; seguidores?: number; seguindo?: number }>(res);
            setData((prev) => {
                if (!prev?.user) return prev;
                return {
                    user: {
                        ...prev.user,
                        isFollowing: body.following,
                        ...(typeof body.seguidores === 'number' ? { seguidores: body.seguidores } : {}),
                        ...(typeof body.seguindo === 'number' ? { seguindo: body.seguindo } : {}),
                    },
                };
            });
        } catch (err) {
            console.error('Erro ao seguir utilizador:', err);
        } finally {
            setFollowLoading(false);
        }
    }, [data?.user, isOwnProfile]);

    if (loading) {
        return (
            <main className="min-h-screen bg-neutral-950 pt-28 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </main>
        );
    }
    if (notFoundState || !data || !data.user) {
        if (notFoundState) navigate('/404');
        return null;
    }

    const { user } = data;

    const favorites: any[] = user.favorites ?? [];

    const stats = {
        total_leituras: 0,
        total_lidos: 0,
        total_favoritos: favorites.length,
        total_comentarios: commentsFeedMeta.totalItems,
        total_lendo: 0,
    };

    return (
        <div className="min-h-screen bg-neutral-950 pb-20 md:pb-0">
            <Navbar />

            {/* Hero Profile Section */}
            <PerfilHeader
                user={user}
                isOwner={false}
                stats={stats}
                rankingPosicao={rankingPosicao}
                followActions={{
                    canToggle: !isOwnProfile,
                    isFollowing: user.isFollowing ?? false,
                    loading: followLoading,
                    onToggle: handleFollowToggle,
                }}
            />

            {/* Navigation Tabs */}
            <PerfilTabs
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />

            {/* Content Area */}
            <div className="container mx-auto px-4 md:px-6 py-8">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.2 }}
                    >
                        {activeTab === 'colecao' && (
                            <PerfilColecaoTab profileUsrId={user.usr_id} />
                        )}

                        {activeTab === 'listas' && (
                            <PerfilListasPublicasTab
                                profileUsrId={user.usr_id}
                                profileNick={user.usr_nick}
                            />
                        )}

                        {activeTab === 'comentarios' && (
                            <div className="max-w-4xl mx-auto">
                                <div className="mb-6 md:mb-8 px-1">
                                    <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">Comentários</h2>
                                    <p className="text-white/40 text-xs md:text-sm mt-1">
                                        {commentsFeedMeta.totalItems} comentário(s) no site
                                    </p>
                                </div>
                                <ChapterComments
                                    perfilUsuarioId={user.usr_id}
                                    pagina={commentsPage}
                                    onPaginationMeta={meta =>
                                        setCommentsFeedMeta({
                                            totalItems: meta.totalItems,
                                            totalPages: meta.totalPages,
                                        })
                                    }
                                />
                                {commentsFeedMeta.totalPages > 1 ? (
                                    <div className="flex flex-wrap items-center justify-center gap-3 mt-8 pb-4">
                                        <button
                                            type="button"
                                            disabled={commentsPage <= 1}
                                            onClick={() => setCommentsPage(p => Math.max(1, p - 1))}
                                            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:border-orange-500/40 transition-colors"
                                        >
                                            Anterior
                                        </button>
                                        <span className="text-white/50 text-sm">
                                            Página {commentsPage} de {commentsFeedMeta.totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            disabled={commentsPage >= commentsFeedMeta.totalPages}
                                            onClick={() =>
                                                setCommentsPage(p =>
                                                    Math.min(commentsFeedMeta.totalPages, p + 1),
                                                )
                                            }
                                            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:border-orange-500/40 transition-colors"
                                        >
                                            Seguinte
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {activeTab === 'tags' && (
                            <PerfilTagsTab
                                groupedTags={groupedTags}
                                userName={user.usr_nome}
                            />
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
}
